/**
 * How the calendar draws itself.
 *
 * The original shipped three of these and let the clinic choose — the choice
 * lived in the calendar's own toolbar at first and then moved to
 * Setări → Aspect, where a decision made once belongs. They are not themes:
 * the colours are the same in all four. What changes is the shape of an
 * appointment card and how tightly the day is packed, which is the difference
 * between a clinic with thirty bookings a day and one with two hundred.
 *
 * Stored and applied exactly like `appearance`: on the root element, mirrored
 * into `localStorage` so `index.html` can stamp it before first paint, and
 * held per clinic in `orgConfig` because it is the clinic's choice, not one
 * person's.
 */
export const CAL_STYLES = [
  {
    key: '',
    label: 'Compact',
    desc: 'Rânduri de 19px, fâșie de status de 3px, statusul scris cu litere, fără umbră. Modul în care lucrează clinica — cel pe care îl știe drept „EasyPlan". Pentru zile cu sute de programări.',
  },
  {
    key: 'columns',
    label: 'Coloane egale',
    desc: 'Colț mare, umbră ușoară, fundal palid, statusul ca pictogramă în colț. Cardul nu coboară sub 26px.',
  },
  {
    key: 'full',
    label: 'Lățime plină',
    desc: 'Card plin, în culoarea serviciului, cu text alb și contur de status. Se vede de la distanță.',
  },
] as const;

export type CalStyleKey = (typeof CAL_STYLES)[number]['key'];

const STORAGE_KEY = 'df.calStyle';

const isKnown = (v: string): v is CalStyleKey => CAL_STYLES.some((s) => s.key === v);

export function applyCalStyle(value: string) {
  const v = isKnown(value) ? value : '';
  const root = document.documentElement;
  if (v) root.dataset.calStyle = v;
  else delete root.dataset.calStyle;
  try {
    if (v) localStorage.setItem(STORAGE_KEY, v);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode: it applies now and reverts on the next load, which is
    // better than refusing to apply it at all.
  }
}

/**
 * „Ridică programarea de sub cursor".
 *
 * Într-o suprapunere, cardul din spate rămâne cu două litere din nume.
 * Pornită, funcția îl întinde pe toată coloana cât timp cursorul e pe el —
 * cum făcea aplicația veche. E o alegere, fiindcă are un preț: cardul ridicat
 * acoperă vecinii, deci drumul mouse-ului spre cel de alături trece peste el.
 * O clinică cu zile aglomerate o vrea; una care programează rar, nu.
 *
 * Aceeași cale ca stilul calendarului: atribut pe rădăcină, oglindit în
 * `localStorage` ca `index.html` să-l pună înainte de prima pictare, și ținut
 * în `orgConfig` fiindcă e alegerea clinicii.
 */
const LIFT_KEY = 'df.calLift';

export function applyCalLift(on: boolean) {
  const root = document.documentElement;
  if (on) root.dataset.calLift = 'on';
  else delete root.dataset.calLift;
  try {
    if (on) localStorage.setItem(LIFT_KEY, 'on');
    else localStorage.removeItem(LIFT_KEY);
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare, ceea ce e mai bine
    // decât să refuzăm aplicarea.
  }
}

/**
 * Pulsul de atenție al recepției.
 *
 * Cardul unei programări pulsează — roșu când a trecut un sfert de oră și
 * pacientul n-a venit, nici n-a anunțat; verde cât stă în sala de așteptare.
 * E un semnal de lucru, nu o podoabă, dar tot o mișcare pe ecran: o clinică
 * mică, unde recepția vede toată ziua dintr-o privire, îl poate găsi mai
 * degrabă un zgomot decât un ajutor.
 *
 * Pornit implicit, fiindcă așa a fost cerut și așa lucrează clinicile pline.
 * De aceea, spre deosebire de celelalte, atributul marchează *oprirea*:
 * absența lui înseamnă „pulsul e pornit". Aceeași cale ca stilul calendarului
 * — atribut pe rădăcină, oglindit în `localStorage` ca `index.html` să-l pună
 * înainte de prima pictare, și ținut în `orgConfig` fiindcă e alegerea clinicii.
 */
const PULSE_KEY = 'df.calPulse';

export function applyCalPulse(on: boolean) {
  const root = document.documentElement;
  if (on) delete root.dataset.calPulse;
  else root.dataset.calPulse = 'off';
  try {
    if (on) localStorage.removeItem(PULSE_KEY);
    else localStorage.setItem(PULSE_KEY, 'off');
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare, ceea ce e mai bine
    // decât să refuzăm aplicarea.
  }
}

/**
 * Forma pulsului: câte pulsuri dă o serie, cât de repede clipește fiecare, și
 * cât de des se repetă seria. Alese de clinică, aplicate ca atribute pe
 * rădăcină (Grid.css le traduce în animație), oglindite în `localStorage` ca
 * `index.html` să le pună înainte de prima pictare. Implicitul — 3 pulsuri,
 * normale, la 10s — nu scrie niciun atribut, deci un card nemodificat
 * pulsează exact ca înainte.
 */
export type CalPulseSpeed = 'lent' | 'normal' | 'rapid';
export const CAL_PULSE_COUNTS = [1, 2, 3] as const;
export const CAL_PULSE_SPEEDS: CalPulseSpeed[] = ['lent', 'normal', 'rapid'];
export const CAL_PULSE_CYCLES = [4, 6, 8, 10, 12, 15, 20] as const;
export const CAL_PULSE_DEFAULT = { count: 3, speed: 'normal' as CalPulseSpeed, cycle: 10 };

const BURST_KEY = 'df.calPulseBurst';
const CYCLE_KEY = 'df.calPulseCycle';

export function applyCalPulseBurst(count: number, speed: CalPulseSpeed) {
  const root = document.documentElement;
  const isDefault = count === CAL_PULSE_DEFAULT.count && speed === CAL_PULSE_DEFAULT.speed;
  const value = `${count}-${speed}`;
  if (isDefault) delete root.dataset.calPulseBurst;
  else root.dataset.calPulseBurst = value;
  try {
    if (isDefault) localStorage.removeItem(BURST_KEY);
    else localStorage.setItem(BURST_KEY, value);
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare.
  }
}

export function applyCalPulseCycle(cycle: number) {
  const root = document.documentElement;
  const isDefault = cycle === CAL_PULSE_DEFAULT.cycle;
  if (isDefault) delete root.dataset.calPulseCycle;
  else root.dataset.calPulseCycle = String(cycle);
  try {
    if (isDefault) localStorage.removeItem(CYCLE_KEY);
    else localStorage.setItem(CYCLE_KEY, String(cycle));
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare.
  }
}

/**
 * Evidențierea cardului abia creat — inelul verde și eticheta „NOU", 2,5s.
 *
 * Pornită implicit, ca pulsul: absența atributului înseamnă „pornit", iar
 * `data-cal-newappt='off'` o oprește. Aceeași cale — atribut pe rădăcină,
 * oglindit în `localStorage` ca `index.html` să nu lase cardul să sclipească
 * o clipă la fiecare încărcare.
 */
const NEWAPPT_KEY = 'df.calNewAppt';

export function applyCalNewHighlight(on: boolean) {
  const root = document.documentElement;
  if (on) delete root.dataset.calNewappt;
  else root.dataset.calNewappt = 'off';
  try {
    if (on) localStorage.removeItem(NEWAPPT_KEY);
    else localStorage.setItem(NEWAPPT_KEY, 'off');
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare.
  }
}

/**
 * „Mod calm" — aceeași liniște ca `prefers-reduced-motion`, dar aleasă de
 * clinică din aplicație, pentru cine n-are setarea în sistemul de operare.
 *
 * Oprit implicit: absența atributului înseamnă mișcarea obișnuită, iar
 * `data-calm` o taie. Aplicat la nivelul întregii aplicații, nu doar al
 * calendarului — atinge modale, panouri, tooltip-uri — de aceea CSS-ul stă în
 * `global.css`, nu în grila calendarului.
 */
const CALM_KEY = 'df.calm';

export function applyCalmMode(on: boolean) {
  const root = document.documentElement;
  if (on) root.dataset.calm = '';
  else delete root.dataset.calm;
  try {
    if (on) localStorage.setItem(CALM_KEY, 'on');
    else localStorage.removeItem(CALM_KEY);
  } catch {
    // Mod privat: se aplică acum și revine la reîncărcare.
  }
}
